import { RELAY_HOSTNAME, RELAY_SUBSCRIBE } from "cfg/endpoints";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Message, jsonSafeToMessage } from "interfaces/relay";

export const useLiveStatus = (
    onMessage: (message: Message) => void,
    {
        abort = false,
        resourceIds = '',  // comma-separated user ids
    }: {abort?: boolean, resourceIds?: string} = {},
) => {
    const socket = useRef<WebSocket | null>(null)
    const connecting = useRef<boolean>(false)
    const [socketClosed, setSocketClosed] = useState<boolean>(false)
    const apiKey = useSelector((state: RootState) => state.authenticationInfo.value?.apiKey)
    const teamId = useSelector((state: RootState) => state.team.value?.team_id)
    const selectedTeamId = useSelector((state: RootState) => state.provisionedTeamSelector.selectedTeamId)
    
    // Use selected team ID if available, otherwise fall back to user's team ID
    const effectiveTeamId = selectedTeamId || teamId

    useEffect(() => {
        if (!apiKey) return
        if (!effectiveTeamId) return
        if (abort) return
        connectToSocket()
        return () => {  // close when params change and let auto-reconnect handle it
            socket.current?.close()
        }
    }, [apiKey, effectiveTeamId, resourceIds, abort])

    const connectToSocket = useCallback(() => {
        if (!apiKey) return
        if (!effectiveTeamId) return
        if (abort) return
        if (socket.current && socket.current.readyState === WebSocket.OPEN) return
        if (connecting.current) return
        connecting.current = true
        const queryParams = new URLSearchParams({
            team_id: effectiveTeamId,
        })
        if (resourceIds) {
            queryParams.set('resource_ids', resourceIds)
        }
        const socketUrl = `wss://${RELAY_HOSTNAME}/${RELAY_SUBSCRIBE}?${queryParams.toString()}`
        const ws = socket.current = new WebSocket(socketUrl)
        ws.onopen = () => {
            connecting.current = false
            ws.send(JSON.stringify({'api_key': apiKey}))
            if (socket.current === ws) { backoffMs.current = 1000 }
        }
        ws.onmessage = handleMessage
        ws.onclose = () => {
            if (socket.current === ws) {
                socket.current = null
                connecting.current = false
                setSocketClosed(true)
            }
        }
        setSocketClosed(false)
    }, [apiKey, effectiveTeamId, resourceIds, abort])

    // reconnect but be polite about it
    const backoffMs = useRef<number>(500)
    const reconnectTimer = useRef<NodeJS.Timeout | null>(null)
    useEffect(() => {
        if (!socketClosed) return
        if (reconnectTimer.current !== null) return
        reconnectTimer.current = setTimeout(() => {
            reconnectTimer.current = null
            if (!socket.current || socket.current.readyState >= WebSocket.CLOSING) {
                backoffMs.current *= 2
                connectToSocket()
            }
        }, backoffMs.current)
    }, [socketClosed])

    const handleMessage = (event: MessageEvent) => {
        const data = JSON.parse(event.data)
        const message = jsonSafeToMessage(data)
        // debug metadata to verify prospect connection and end flags
        try {
            if (message?.message_type === 'SESSION_STATUS') {
                const md: any = (message as any).metadata
                // Log only tiny summary for noise control
                // joined_at/left_at to infer connectionIsConnected
                // ended_at for session termination
                // Note: using console.debug to allow filtering in prod consoles
                console.debug('[LiveStatus] session update', {
                    session_id: (message as any)?.session?.session_id,
                    customer_joined_at: md?.customer?.joined_at,
                    customer_left_at: md?.customer?.left_at,
                    customer_removed_at: md?.customer?.removed_at,
                    ended_at: md?.ended_at,
                })
            }
        } catch {}
        onMessage(message)
    }
}